import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import {
  SIDEBAR_LAYOUT_OPTIONS,
  normalizeSidebarLayout,
  SidebarLayoutMode,
} from '../../config/sidebarLayout';

const shell = 'bg-[#0a1424]';
const panel = 'bg-slate-50 dark:bg-slate-900/40';
const topbar = 'bg-white dark:bg-slate-800';
const accent = 'bg-[#6b8fb8]';
const muted = 'bg-slate-300 dark:bg-slate-600';
const navItem = 'rounded-full bg-white/20';
const navActive = 'rounded-full bg-[#6b8fb8]';

function LayoutPreview({ mode }: { mode: SidebarLayoutMode }) {
  const resolved = normalizeSidebarLayout(mode) ?? 'classic';

  if (resolved === 'topmenu') {
    return (
      <div className="relative flex h-28 w-full flex-col overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
        <div
          className={cn(
            'flex h-7 items-center gap-1.5 border-b border-black/5 px-2 dark:border-white/5',
            topbar,
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', accent)} />
          <span className={cn('h-2 flex-1 rounded-md opacity-90', accent)} />
          <span className={cn('h-2 w-8 rounded-md opacity-40', muted)} />
          <span className={cn('h-2 w-8 rounded-md opacity-40', muted)} />
          <span className="ml-auto h-3 w-3 rounded-full bg-black/5 dark:bg-white/10" />
        </div>
        <div className={cn('flex flex-1 flex-col gap-1.5 p-2.5', panel)}>
          <div className={cn('h-1.5 w-16 rounded-full opacity-60', muted)} />
          <div className="mt-0.5 flex-1 rounded-lg border border-black/5 bg-white/60 p-2 dark:border-white/5 dark:bg-white/5">
            <div className={cn('mb-1.5 h-1 w-3/4 rounded-full opacity-40', muted)} />
            <div className={cn('mb-1 h-1 w-full rounded-full opacity-25', muted)} />
            <div className={cn('h-1 w-2/3 rounded-full opacity-25', muted)} />
          </div>
        </div>
      </div>
    );
  }

  if (resolved === 'sidebar_topmenu') {
    return (
      <div className="relative flex h-28 w-full overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
        <div className={cn('flex w-[22%] flex-col gap-1 p-1.5', shell)}>
          <div className={cn('h-1.5 w-full', navActive)} />
          <div className={cn('h-1 w-full', navItem)} />
          <div className={cn('h-1 w-3/4', navItem)} />
          <div className={cn('h-1 w-full', navItem)} />
          <div className={cn('mt-auto h-1.5 w-full opacity-70', accent)} />
        </div>
        <div className={cn('flex min-w-0 flex-1 flex-col', panel)}>
          <div
            className={cn(
              'flex h-6 items-center gap-1 border-b border-black/5 px-1.5 dark:border-white/5',
              topbar,
            )}
          >
            <span className={cn('h-1.5 flex-1 rounded-md opacity-90', accent)} />
            <span className={cn('h-1.5 w-6 rounded-md opacity-40', muted)} />
            <span className={cn('h-1.5 w-6 rounded-md opacity-40', muted)} />
          </div>
          <div className="flex flex-1 flex-col gap-1 p-2">
            <div className={cn('h-1 w-12 rounded-full opacity-50', muted)} />
            <div className="flex-1 rounded-md border border-black/5 bg-white/60 dark:border-white/5 dark:bg-white/5" />
          </div>
        </div>
      </div>
    );
  }

  if (resolved === 'rail') {
    return (
      <div className="relative flex h-28 w-full overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
        <div className={cn('flex w-7 flex-col items-center gap-1.5 py-2', shell)}>
          <span className={cn('h-2.5 w-2.5 rounded-md', accent)} />
          <span className="h-2.5 w-2.5 rounded-md bg-white/25" />
          <span className="h-2.5 w-2.5 rounded-md bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-md bg-white/15" />
          <span className={cn('mt-auto h-2.5 w-2.5 rounded-full opacity-70', accent)} />
        </div>
        <div className={cn('flex min-w-0 flex-1 flex-col', panel)}>
          <div
            className={cn(
              'flex h-6 items-center gap-1.5 border-b border-black/5 px-2 dark:border-white/5',
              topbar,
            )}
          >
            <span className={cn('h-1 w-10 rounded-full opacity-50', muted)} />
            <span className="ml-auto h-3 w-3 rounded-full bg-black/5 dark:bg-white/10" />
          </div>
          <div className="flex flex-1 flex-col gap-1.5 p-2.5">
            <div className={cn('h-1.5 w-16 rounded-full opacity-60', muted)} />
            <div className="mt-0.5 flex-1 rounded-lg border border-black/5 bg-white/60 p-2 dark:border-white/5 dark:bg-white/5">
              <div className={cn('mb-1.5 h-1 w-3/4 rounded-full opacity-40', muted)} />
              <div className={cn('mb-1 h-1 w-full rounded-full opacity-25', muted)} />
              <div className={cn('h-1 w-2/3 rounded-full opacity-25', muted)} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // classic
  return (
    <div className="relative flex h-28 w-full overflow-hidden rounded-xl border border-black/10 shadow-inner dark:border-white/10">
      <div className={cn('flex w-[32%] flex-col gap-1 p-2', shell)}>
        <div className={cn('h-1.5 w-full rounded-full opacity-90', accent)} />
        <div className={cn('mt-1 h-1.5 w-full', navActive)} />
        <div className={cn('ml-1.5 h-1 w-4/5', navItem)} />
        <div className={cn('ml-1.5 h-1 w-3/5', navItem)} />
        <div className={cn('mt-0.5 h-1 w-full', navItem)} />
        <div className={cn('h-1 w-4/5', navItem)} />
        <div className={cn('mt-auto h-1.5 w-full rounded-full opacity-70', accent)} />
      </div>
      <div className={cn('flex min-w-0 flex-1 flex-col', panel)}>
        <div
          className={cn(
            'flex h-6 items-center gap-1.5 border-b border-black/5 px-2 dark:border-white/5',
            topbar,
          )}
        >
          <span className={cn('h-1 w-10 rounded-full opacity-50', muted)} />
          <span className="ml-auto h-3 w-3 rounded-full bg-black/5 dark:bg-white/10" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-2.5">
          <div className={cn('h-1.5 w-16 rounded-full opacity-60', muted)} />
          <div className="mt-0.5 flex-1 rounded-lg border border-black/5 bg-white/60 p-2 dark:border-white/5 dark:bg-white/5">
            <div className={cn('mb-1.5 h-1 w-3/4 rounded-full opacity-40', muted)} />
            <div className={cn('mb-1 h-1 w-full rounded-full opacity-25', muted)} />
            <div className={cn('h-1 w-2/3 rounded-full opacity-25', muted)} />
          </div>
        </div>
      </div>
    </div>
  );
}

type SidebarLayoutPickerProps = {
  value: SidebarLayoutMode | null | undefined;
  orgDefault: SidebarLayoutMode;
  onChange: (value: SidebarLayoutMode) => void;
  className?: string;
};

/**
 * Visual navigation layout picker — matches Theme / Bookmarks card design.
 */
export function SidebarLayoutPicker({
  value,
  orgDefault,
  onChange,
  className,
}: SidebarLayoutPickerProps) {
  const { colors } = useTheme();
  const effective =
    normalizeSidebarLayout(value) ?? normalizeSidebarLayout(orgDefault) ?? 'classic';

  return (
    <div
      role="radiogroup"
      aria-label="Navigation layout"
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}
    >
      {SIDEBAR_LAYOUT_OPTIONS.map((option) => {
        const selected = effective === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={cn(
              'group relative rounded-2xl border-2 p-3 text-left transition-all',
              selected
                ? cn(colors.primaryBorderAll, 'bg-slate-50/90 shadow-sm dark:bg-white/[0.04]')
                : cn(colors.border, 'border', colors.interactiveHover),
            )}
          >
            <LayoutPreview mode={option.id} />
            <div className="mt-3 flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  selected
                    ? cn(colors.primary, colors.primaryText, 'border-transparent')
                    : cn(colors.border, 'bg-transparent'),
                )}
                aria-hidden
              >
                {selected && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', colors.text)}>
                  {option.label}
                </span>
                <span className={cn('mt-0.5 block text-xs leading-snug', colors.textMuted)}>
                  {option.description}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default SidebarLayoutPicker;
